/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-07-22 00:00:00
 *
 *******************************************************/
//@flow

import _ from 'lodash';

import React, { Component } from 'react';
import {
  Card,
  Button,
  TextStyle,
  TextContainer,
  FormLayout,
  SkeletonDisplayText,
  SkeletonBodyText
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import StarRatings from 'react-star-ratings';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { hasTag, isExceedLength } from 'src/helpers/form-validation-helper';
import { URL } from 'src/constants/links';
import imgExpReviewRating from 'src/assets/images/dashboard/expReviewRating/expReviewRating.png';
import imgExpReviewRatingSubmittedFeedback from 'src/assets/images/dashboard/expReviewRating/expReviewRatingSubmittedFeedback.png';
import imgExpReviewRatingSubmittedFeedback2 from 'src/assets/images/dashboard/expReviewRating/expReviewRatingSubmittedFeedback2.png';
import tadaIcon from 'src/assets/images/dashboard/expReviewRating/tada-icon.png';
import * as expReviewRatingActions from 'actions/exp-review-rating-actions';

const isExceedLengthValue = isExceedLength(10000);

type Props = {
  loading: boolean
};

type State = {
  step: string,
  rating: number
};

type ComposedProps = Props & WithI18nProps;
class ExpReviewRating extends Component<ComposedProps, State> {
  state = {
    step: 'rating',
    rating: 0
  };

  componentDidMount() {
    const { getReviewRatingStatus } = this.props;
    getReviewRatingStatus();
  }

  componentWillReceiveProps(nextProps) {
    const { reviewRatingStatusLoading } = this.props;
    const { reviewRatingStatusLoading: nextReviewRatingStatusLoading, step, rating } = nextProps;
    const { step: stepFromState } = this.state;

    if (reviewRatingStatusLoading && !nextReviewRatingStatusLoading) {
      if (step === 'submitted' && stepFromState !== step) {
        this.setState({ step: 'submitted', rating: 5 });
      }
    }

    if (
      this.props.submitFeedbackLoading &&
      !nextProps.submitFeedbackLoading &&
      nextProps.submitFeedbackSuccess
    ) {
      // Display submitted message
      this.setState({ step: 'submitted', rating });
    }
  }

  renderRatingLayout = () => {
    const { i18n } = this.props;

    return (
      <div className="layoutCols">
        <div className="firstCol">
          <p style={{ fontSize: '0.9375rem' }}>
            {i18n.translate('AdminHTML.Dashboard.ExpReviewRating.question_p1')}{' '}
          </p>
          <p style={{ fontSize: '0.9375rem' }}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.Dashboard.ExpReviewRating.question_p2')}
            </TextStyle>{' '}
            {i18n.translate('AdminHTML.Dashboard.ExpReviewRating.question_p3')}
          </p>
          <span style={{ padding: '0.875rem 0' }}>
            <StarRatings
              rating={this.state.rating}
              starRatedColor="rgb(255, 208, 2)"
              starHoverColor="rgb(255, 208, 2)"
              starDimension="32px"
              changeRating={this.changeRating}
              numberOfStars={5}
              name="rating"
            />
          </span>

          {i18n.translate('AdminHTML.Dashboard.ExpReviewRating.explain')}
        </div>
        <div className="secondCol">
          <img src={imgExpReviewRating} alt="" />
        </div>
      </div>
    );
  };

  renderFeedbackForm = () => {
    const { i18n, pristine, submitting, submitFeedbackLoading, handleSubmit } = this.props;

    return (
      <TextContainer>
        <p style={{ paddingBottom: '8px' }}>
          <TextStyle variation="strong">
            {i18n.translate('AdminHTML.Dashboard.ExpReviewRating.submitForm_Label')}
          </TextStyle>
        </p>
        <form onSubmit={this.handleDefaultSubmit}>
          <FormLayout>
            <Field
              name="content"
              component={RenderTextField}
              placeholder={i18n.translate(
                'AdminHTML.Dashboard.ExpReviewRating.submitForm_Placeholder'
              )}
              anotherProps={{ multiline: 2 }}
              validate={[hasTag, isExceedLengthValue]}
            />
          </FormLayout>
        </form>
        <div>
          <Button
            disabled={pristine || submitting}
            loading={submitFeedbackLoading && !pristine}
            onClick={handleSubmit(this.handleSubmitFeedback.bind(this))}>
            {i18n.translate('AdminHTML.Dashboard.ExpReviewRating.submitForm_Button')}
          </Button>
        </div>
      </TextContainer>
    );
  };

  renderSubmittedMessage = () => {
    const { i18n } = this.props;
    const { rating = 0 } = this.state;

    return (
      <div className={rating < 5 ? 'layoutCols submitted' : 'layoutCols lowRating'}>
        <div className="firstCol">
          <p style={{ fontSize: '0.9375rem' }}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.Dashboard.ExpReviewRatingSubmittedFeedback.title')}
            </TextStyle>{' '}
            <img
              style={
                rating < 5
                  ? { position: 'absolute', top: '28px', left: '195px', width: '16px' }
                  : { position: 'absolute', top: '16px', left: '195px', width: '16px' }
              }
              src={tadaIcon}
              alt=""
            />
          </p>
          <p
            style={
              rating < 5
                ? { fontSize: '0.9375rem', marginTop: '8px', marginBottom: '15px' }
                : { fontSize: '0.9375rem', marginTop: '8px' }
            }>
            {i18n.translate('AdminHTML.Dashboard.ExpReviewRatingSubmittedFeedback.content')}
          </p>

          {/* Button Rate on Shopify */}
          {rating === 5 ? (
            <span style={{ marginTop: '-7px' }}>
              <Button primary onClick={this.rateOnShopify}>
                {i18n.translate('AdminHTML.Dashboard.ExpReviewRating.rateUsOnShopify')}
              </Button>
            </span>
          ) : (
            ''
          )}
        </div>
        <div className={rating < 5 ? 'secondCol' : 'secondCol week2'}>
          <img
            src={
              rating < 5
                ? imgExpReviewRatingSubmittedFeedback
                : imgExpReviewRatingSubmittedFeedback2
            }
            alt=""
          />
        </div>
      </div>
    );
  };

  renderComponent = step => {
    const components = {
      rating: this.renderRatingLayout(),
      feedback: this.renderFeedbackForm(),
      submitted: this.renderSubmittedMessage()
    };

    return components[step];
  };

  render() {
    const { step = 'rating' } = this.state;
    const { reviewRatingStatusLoading } = this.props;
    return (
      <div className="BC-CalloutCard-ExpReviewRating__Dashboard">
        <Card>
          <Card.Section>
            {!reviewRatingStatusLoading ? (
              this.renderComponent(step)
            ) : (
              <TextContainer>
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText />
              </TextContainer>
            )}
          </Card.Section>
        </Card>
      </div>
    );
  }

  changeRating = newRating => {
    this.setState({
      rating: newRating,
      step: 'feedback'
    });
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  handleSubmitFeedback = data => {
    const { rating = 0 } = this.state;
    const { content = '' } = data;

    const body = { rating, message: content };
    this.props.submitFeedback(body);
  };

  rateOnShopify = () => {
    window.open(URL.ShopifyRatingPage, '_blank');
  };
}

const mapStateToProps = (state, props) => {
  const { expReviewRating } = state;

  return {
    submitFeedbackLoading: _.get(expReviewRating, 'expReviewRatingLoading', false),
    submitFeedbackSuccess: _.get(expReviewRating, 'expReviewRatingSuccess', false),
    reviewRatingStatusLoading: _.get(expReviewRating, 'expReviewRatingStatusLoading', false),
    step: _.get(expReviewRating, 'step', 'rating'),
    rating: _.get(expReviewRating, 'rating', 0)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  submitFeedback: data => {
    dispatch(expReviewRatingActions.sendReviewRatingFeedback(data, ownProps.i18n));
  },
  resetFeedback: () => {
    dispatch(expReviewRatingActions.resetReviewRatingFeedback());
  },
  getReviewRatingStatus: () => {
    dispatch(expReviewRatingActions.getReviewRatingStatus());
  }
});

const ExpReviewRatingForm = reduxForm({
  form: 'ExpReviewRatingForm', // a unique identifier for this form
  enableReinitialize: true
})(ExpReviewRating);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ExpReviewRatingForm));
